/*
 * BORRADOR DE UNA CAPTURA EN CURSO (RF-084, D-85, RN-144, RN-145).
 *
 * QUÉ ES Y QUÉ NO ES:
 *   * ES resguardo local de un formulario en curso, para que salir a otra app del
 *     teléfono —o que el sistema descarte la pestaña— no cueste lo escrito.
 *   * NO ES captura offline. Sigue vigente D-02 (RNF-041): no hay cola de
 *     sincronización, no hay escritura diferida y guardar sigue exigiendo conexión
 *     y respuesta del servidor. El borrador NO viaja al servidor, no se comparte
 *     con la otra persona y no aparece en ninguna consulta ni en ningún total
 *     (RN-144d).
 *
 * DÓNDE VIVE: en el almacenamiento local del navegador de ESTE dispositivo, nunca
 * en base de datos (RN-144a). Precedentes vigentes del mismo tipo: el tema (D-08)
 * y la zona horaria (D-06).
 * [CORREGIDO 2026-10-03 · RN-450, RN-434] Esta nota citaba también «la cuenta
 * preferida de la captura rápida (D-43)», que quedó huérfana: el incremento 09
 * retiró esa preferencia y su módulo completos, porque la cuenta de la captura
 * ahora llega vacía (RF-153, D-247).
 *
 * QUÉ SE GUARDA: el contenido de los campos y el instante de la última escritura.
 * NUNCA credenciales ni la cookie de sesión (RN-144c).
 *
 * PROBABLE SIN ESPERAR 24 HORAS: todas las funciones reciben `almacen` y `ahora`
 * inyectables. La caducidad se ejercita moviendo `ahora`, no el reloj del equipo.
 */

/** RN-145b: con 24 horas o más sin modificación el borrador está vencido. */
export const MILISEGUNDOS_DE_VIDA_DEL_BORRADOR = 24 * 60 * 60 * 1000;

/** Prefijo común: permite barrer sólo borradores sin tocar tema ni preferencias. */
export const PREFIJO_DE_BORRADOR = 'gp.borrador.';

/** Formularios que generan borrador en este lote (RN-128). */
export const FORMULARIOS_CON_BORRADOR = Object.freeze({
  CUENTA: 'CUENTA',
  TARJETA: 'TARJETA',
  PLAN_MSI: 'PLAN_MSI',
});

/** Resultados posibles de leer un borrador. */
export const RESULTADO = Object.freeze({
  SIN_BORRADOR: 'SIN_BORRADOR',
  VENCIDO: 'VENCIDO',
  VIGENTE: 'VIGENTE',
});

/**
 * Clave del borrador (RN-144b): por DISPOSITIVO (es almacenamiento local), por
 * PERSONA USUARIA en sesión, por FORMULARIO y, en edición, por el IDENTIFICADOR
 * del registro que se está editando.
 *
 * Dos formularios distintos abiertos en momentos distintos tienen borradores
 * independientes y no se pisan: el de Nueva cuenta no pisa al de Compra a meses.
 */
export function claveDeBorrador({ formulario, idRegistro = null, idUsuario = null }) {
  if (!formulario) throw new Error('claveDeBorrador exige el formulario.');
  const persona = idUsuario === null || idUsuario === undefined ? 'anonimo' : String(idUsuario);
  const registro = idRegistro === null || idRegistro === undefined || idRegistro === ''
    ? 'nueva'
    : String(idRegistro);
  return `${PREFIJO_DE_BORRADOR}${persona}.${formulario}.${registro}`;
}

/**
 * Escribe el borrador. Se llama conforme se captura, de modo que un cierre abrupto
 * pierda a lo sumo la última tecla, no el formulario (RN-144e).
 *
 * `esquema` identifica la forma del formulario: si una versión nueva de la
 * aplicación cambia los campos, el borrador viejo se descarta como vencido y en
 * silencio (RN-145f).
 */
export function guardarBorrador(clave, valores, { almacen = obtenerAlmacen(), ahora = Date.now, esquema = null } = {}) {
  if (!almacen || !clave) return false;
  try {
    almacen.setItem(clave, JSON.stringify({ e: esquema, t: ahora(), v: valores }));
    return true;
  } catch {
    // Un navegador sin almacenamiento (o con la cuota llena) no rompe la captura:
    // sólo se queda sin red de seguridad. Guardar sigue funcionando igual.
    return false;
  }
}

/**
 * Lee el borrador y resuelve su vigencia (RN-145).
 *
 * Devuelve siempre `{ estado, valores, instante, edadEnMs }`:
 *   * SIN_BORRADOR — no había nada guardado.
 *   * VENCIDO      — había, pero tiene 24 h o más sin modificación, o su
 *                    estructura ya no corresponde al formulario. SE ELIMINA EN ESE
 *                    MOMENTO y en silencio: no se restaura y NO se muestra la banda
 *                    de "se recuperó un borrador" (RN-145c, RN-145d, RN-145f).
 *   * VIGENTE      — menos de 24 h: se restaura y sí se avisa (RN-132b).
 */
export function leerBorrador(clave, { almacen = obtenerAlmacen(), ahora = Date.now, esquema = null } = {}) {
  if (!almacen || !clave) return { estado: RESULTADO.SIN_BORRADOR, valores: null, instante: null, edadEnMs: null };

  let crudo = null;
  try {
    crudo = almacen.getItem(clave);
  } catch {
    return { estado: RESULTADO.SIN_BORRADOR, valores: null, instante: null, edadEnMs: null };
  }
  if (!crudo) return { estado: RESULTADO.SIN_BORRADOR, valores: null, instante: null, edadEnMs: null };

  let guardado = null;
  try {
    guardado = JSON.parse(crudo);
  } catch {
    return descartar(clave, almacen);
  }

  // Estructura ilegible, sin sello de tiempo o de un esquema que ya no existe:
  // se trata como vencido, en silencio (RN-145f).
  if (!guardado || typeof guardado !== 'object' || typeof guardado.t !== 'number' || !guardado.v) {
    return descartar(clave, almacen);
  }
  if (esquema !== null && guardado.e !== esquema) {
    return descartar(clave, almacen);
  }

  const edadEnMs = ahora() - guardado.t;
  if (edadEnMs >= MILISEGUNDOS_DE_VIDA_DEL_BORRADOR) {
    return descartar(clave, almacen);
  }

  return {
    estado: RESULTADO.VIGENTE,
    valores: guardado.v,
    instante: guardado.t,
    // Un borrador con sello en el futuro (reloj movido) se presenta como recién
    // escrito en lugar de dar una edad negativa.
    edadEnMs: Math.max(0, edadEnMs),
  };
}

/**
 * Borra un borrador. RN-134: se elimina SIN EXCEPCIÓN al guardar con éxito, al
 * cancelar explícitamente, al cerrar sesión y al caducar. Fuera de esos cuatro
 * momentos se conserva.
 */
export function borrarBorrador(clave, { almacen = obtenerAlmacen() } = {}) {
  if (!almacen || !clave) return false;
  try {
    almacen.removeItem(clave);
    return true;
  } catch {
    return false;
  }
}

/**
 * RN-145e: los borradores vencidos de OTROS formularios se limpian también al
 * arrancar la aplicación, para que no se acumulen en el dispositivo. Devuelve
 * cuántos se eliminaron.
 */
export function limpiarBorradoresVencidos({ almacen = obtenerAlmacen(), ahora = Date.now } = {}) {
  let eliminados = 0;
  for (const clave of listarClavesDeBorrador(almacen)) {
    const { estado } = leerBorrador(clave, { almacen, ahora });
    if (estado === RESULTADO.VENCIDO) eliminados += 1;
  }
  return eliminados;
}

/**
 * RN-134c: al cerrar sesión no sobrevive ningún borrador, aunque no hayan pasado
 * las 24 horas. Coherente con RN-045d ("nada de la sesión sobrevive al cierre").
 *
 * Barre SÓLO las claves con el prefijo de borrador: el tema (D-08) y la cuenta
 * preferida (D-43) son del aparato, no de la sesión, y NO se tocan.
 */
export function borrarTodosLosBorradores({ almacen = obtenerAlmacen() } = {}) {
  let eliminados = 0;
  for (const clave of listarClavesDeBorrador(almacen)) {
    if (borrarBorrador(clave, { almacen })) eliminados += 1;
  }
  return eliminados;
}

/** Claves de borrador presentes en el almacén, en orden estable. */
export function listarClavesDeBorrador(almacen = obtenerAlmacen()) {
  if (!almacen) return [];
  try {
    const claves = [];
    if (typeof almacen.key === 'function' && typeof almacen.length === 'number') {
      for (let i = 0; i < almacen.length; i += 1) {
        const clave = almacen.key(i);
        if (typeof clave === 'string' && clave.startsWith(PREFIJO_DE_BORRADOR)) claves.push(clave);
      }
    } else {
      for (const clave of Object.keys(almacen)) {
        if (clave.startsWith(PREFIJO_DE_BORRADOR)) claves.push(clave);
      }
    }
    return claves;
  } catch {
    return [];
  }
}

/**
 * Edad en palabras para la banda de restauración ("se guardó en este teléfono
 * hace 3 minutos"). Presentación pura: no decide nada.
 */
export function describirEdad(milisegundos) {
  const segundos = Math.round(Math.max(0, milisegundos) / 1000);
  if (segundos < 60) return 'hace unos segundos';
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `hace ${minutos} minuto${minutos === 1 ? '' : 's'}`;
  const horas = Math.round(minutos / 60);
  return `hace ${horas} hora${horas === 1 ? '' : 's'}`;
}

/**
 * Un formulario está SUCIO cuando al menos un campo tiene un valor distinto del
 * valor inicial con el que se abrió (RN-128). Cuenta como cambio cualquier edición
 * de la persona, aunque el campo haya quedado vacío después: por eso se compara
 * contra el valor inicial y no contra "vacío".
 *
 * RN-129: un formulario abierto sin ningún cambio NO está sucio, y por tanto no
 * genera borrador, no bloquea la actualización de versión y no pide confirmación.
 */
export function estaSucio(valores, valoresIniciales) {
  if (!valoresIniciales) return false;
  const campos = new Set([...Object.keys(valoresIniciales), ...Object.keys(valores ?? {})]);
  for (const campo of campos) {
    if (normalizar(valores?.[campo]) !== normalizar(valoresIniciales[campo])) return true;
  }
  return false;
}

/**
 * Huella de la forma del formulario: si una versión nueva de la aplicación agrega,
 * quita o renombra campos, el borrador viejo deja de corresponder y se descarta en
 * silencio (RN-145f).
 */
export function esquemaDe(valoresIniciales) {
  return Object.keys(valoresIniciales ?? {}).sort().join('|');
}

function descartar(clave, almacen) {
  borrarBorrador(clave, { almacen });
  return { estado: RESULTADO.VENCIDO, valores: null, instante: null, edadEnMs: null };
}

function normalizar(valor) {
  if (valor === null || valor === undefined) return '';
  return String(valor);
}

function obtenerAlmacen() {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
